import React, { useState, useEffect } from 'react';
import FinanceLayout from '../../../../../../layout/components/FinanceLayout';
import MonthlyDateSelect from '../../../../../../components/AppInput/MonthlyDateSelect';
import { CircularProgress, Dialog, DialogContent } from '@mui/material';
import gstReportServices from '../gstReportServices';

function GSTR3BPage() {

    const [loading, setLoadingStatus] = useState(false);
    const [reportData, setReportData] = useState(null);
    const [filter, setFilter] = useState(null);

    const [dialogStatus, setDialogStatus] = useState(false);
    const [jsonData, setJsonData] = useState(null);

    const getVoucherList = () => {
        setLoadingStatus(true);
        gstReportServices.getGSTR3BSummary(filter).then(res => {
            setReportData(res.data);
        }).catch(error => {
            console.error("GSTR3B Summary loading failed:", error);
        }).finally(() => {
            setLoadingStatus(false);
        });
    };
    
    useEffect(() => {
        if (filter) {
            getVoucherList();
        }
    }, [filter]);

    const getJSON = () => {
        setDialogStatus(true);
        gstReportServices.getGSTR3BJson(filter).then(res => {
            setJsonData(res.payload);
        }).catch(error => {
            console.error("GSTR3B JSON export failed:", error);
        });
    };

    return (
        <FinanceLayout>
            <div className='row my-2'>
                <div className='col-md-6'>
                    <div className="fw-bold h5 py-2">GSTR3B Summary (Self-Assessment Monthly Return)</div>
                </div>

                <div className='col-md-6'>
                    <div className="bg-white p-2 d-flex justify-content-end align-items-center gap-2">
                        <MonthlyDateSelect filter={filter} setFilter={setFilter} />
                        <button className='btn btn-outline-success' onClick={getJSON}>Get JSON</button>
                    </div>
                </div>

                <Dialog
                    open={dialogStatus}
                    keepMounted
                    onClose={() => setDialogStatus(false)}
                    fullWidth={true}
                    maxWidth="lg"
                >
                    <DialogContent>
                        <div className="mb-2 fw-bold text-secondary">Raw GSTR-3B Government Payload Preview:</div>
                        <pre className="bg-light p-3 border rounded text-dark" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                            {JSON.stringify(jsonData, null, 2)}
                        </pre>
                    </DialogContent>
                </Dialog>
            </div>

            {loading && 
              <div className='text-center mt-5'>
                <CircularProgress />
              </div>
            }

            {!loading && reportData && (
                <div className='row my-2'>
                    
                    {/* --- SECTION 1: STATUTORY RETURN BREAKDOWN --- */}
                    <div className='col-md-12 mt-2'>
                        <div className="fw-bold text-secondary mb-1 small">1. Details of Outward Supplies and Inward Supplies Liable to Reverse Charge</div>
                        <table className='table table-bordered table-sm mb-4'>
                            <thead>
                                <tr className='table-dark text-center align-middle'>
                                    <td style={{ width: '5%' }}>Box</td>
                                    <td style={{ width: '45%' }}>Nature of Supplies</td>
                                    <td style={{ width: '13%' }}>Total Taxable Value</td>
                                    <td style={{ width: '12%' }}>Integrated Tax (IGST)</td>
                                    <td style={{ width: '12%' }}>Central Tax (CGST)</td>
                                    <td style={{ width: '13%' }}>State/UT Tax (SGST)</td>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <th className="text-center bg-light small">3.1(a)</th>
                                    <th>Outward Taxable Supplies (Other than Zero/Nil/Exempt)</th>
                                    <td className="text-end">{reportData.outwardSupplies.taxable.toFixed(2)}</td>
                                    <td className="text-end">{reportData.outwardSupplies.igst.toFixed(2)}</td>
                                    <td className="text-end">{reportData.outwardSupplies.cgst.toFixed(2)}</td>
                                    <td className="text-end">{reportData.outwardSupplies.sgst.toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <th className="text-center bg-light small">3.1(d)</th>
                                    <th>Inward Supplies Liable to Reverse Charge (RCM Purchases)</th>
                                    <td className="text-end">{reportData.rcmSupplies.taxable.toFixed(2)}</td>
                                    <td className="text-end text-primary">{reportData.rcmSupplies.igst.toFixed(2)}</td>
                                    <td className="text-end text-primary">{reportData.rcmSupplies.cgst.toFixed(2)}</td>
                                    <td className="text-end text-primary">{reportData.rcmSupplies.sgst.toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <th className="text-center bg-light small">3.1(e)</th>
                                    <th>Non-GST / Nil Rated / Exempted Outward Supplies</th>
                                    <td className="text-end">{reportData.nilRatedOutwardTotal.toFixed(2)}</td>
                                    <td className="text-end text-muted">0.00</td>
                                    <td className="text-end text-muted">0.00</td>
                                    <td className="text-end text-muted">0.00</td>
                                </tr>
                                
                                {/* --- SECTION 2: INPUT TAX CREDIT SUMMARY --- */}
                                <tr className='table-secondary fw-bold'>
                                    <td className="text-center small">4(A)(5)</td>
                                    <td colSpan={2}>Eligible Input Tax Credit Available (All Other ITC)</td>
                                    <td className="text-end text-success">{reportData.itcEligible.igst.toFixed(2)}</td>
                                    <td className="text-end text-success">{reportData.itcEligible.cgst.toFixed(2)}</td>
                                    <td className="text-end text-success">{reportData.itcEligible.sgst.toFixed(2)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {/* --- SECTION 3: NET CASH LIABILITY OFFSETS --- */}
                    <div className='col-md-12'>
                        <div className="fw-bold text-danger mb-1 small">2. Net Tax Payable Cash Ledger Breakdown (Post ITC Cross-Offset)</div>
                        <table className='table table-bordered table-sm mb-4'>
                            <thead>
                                <tr className='table-danger text-center font-weight-bold'>
                                    <td style={{ width: '40%' }}>Tax Ledger Description</td>
                                    <td style={{ width: '20%' }}>IGST Balance</td>
                                    <td style={{ width: '20%' }}>CGST Balance</td>
                                    <td style={{ width: '20%' }}>SGST Balance</td>
                                </tr>
                            </thead>
                            <tbody>
                                <tr className='fw-bold text-end'>
                                    <th className="text-start text-dark">Gross Forward Tax Liabilities Due</th>
                                    <td>{(reportData.outwardSupplies.igst + reportData.rcmSupplies.igst).toFixed(2)}</td>
                                    <td>{(reportData.outwardSupplies.cgst + reportData.rcmSupplies.cgst).toFixed(2)}</td>
                                    <td>{(reportData.outwardSupplies.sgst + reportData.rcmSupplies.sgst).toFixed(2)}</td>
                                </tr>
                                <tr className='text-end text-success'>
                                    <th className="text-start font-weight-normal">Less: Set-off Credit Absorbed (ITC Available)</th>
                                    <td>-{reportData.itcEligible.igst.toFixed(2)}</td>
                                    <td>-{reportData.itcEligible.cgst.toFixed(2)}</td>
                                    <td>-{reportData.itcEligible.sgst.toFixed(2)}</td>
                                </tr>
                            </tbody>
                            <tfoot>
                                <tr className='table-warning fw-bold text-end' style={{ fontSize: '1.05rem' }}>
                                    <th className="text-start text-danger">Net Cash Balance Payable to Bank</th>
                                    <th className="text-end text-danger">{reportData.netCashPayable.igst.toFixed(2)}</th>
                                    <th className="text-end text-danger">{reportData.netCashPayable.cgst.toFixed(2)}</th>
                                    <th className="text-end text-danger">{reportData.netCashPayable.sgst.toFixed(2)}</th>
                                </tr>
                            </tfoot>
                        </table>
                    </div>

                    {/* --- SECTION 4: RECONCILIATION AUDIT MATRIX --- */}
                    <div className='col-md-12 mt-2'>
                        <div className="fw-bold text-secondary mb-1 small">3. Underlying Return Voucher Synchronization Integrity</div>
                        <table className='table table-bordered table-sm bg-white'>
                            <thead>
                                <tr className='table-light'>
                                    <th style={{ width: '50%' }}>GSTR-1 Outward Supply Vouchers Evaluated (Sales / Credits)</th>
                                    <th style={{ width: '50%' }} className="text-center">{reportData.auditInfo.gstr1IncludedVouchers}</th>
                                </tr>
                                <tr className='table-light'>
                                    <th>GSTR-2 Inward Supply Vouchers Evaluated (Purchases / Debits)</th>
                                    <th className="text-center">{reportData.auditInfo.gstr2IncludedVouchers}</th>
                                </tr>
                            </thead>
                        </table>
                    </div>

                </div>
            )}
        </FinanceLayout>
    );
}

export default GSTR3BPage;